// THE YELLOW BORDER (owner, 2026-09-30 — `docs/decisions.md` D-248): *"a red
// border appears when a required field is empty. Then, we could use a yellow
// border on fields that tie back to active, unresolved flags."*
//
// The two colours, stated once:
//  - RED: a required field is empty (`./required.ts`). Nothing else turns a
//    field red.
//  - YELLOW: the field holds a value tied to a flag that is not resolved yet.
//    It clears when the flag is marked resolved. RED BEATS YELLOW on a field
//    that is both empty and flagged (`fieldBorder` below).
//
// This file owns which flag ties to which field. Every screen that borders
// these fields asks it, so the Import Control editor and the read-only
// Invoice Summary cannot disagree. Unlike red, yellow does not depend on
// whether the viewer can edit the field: it says something about the value,
// and the value is the same whoever looks at it.

import { flagDetailSequence, flagLabel } from './constants'

/** The ten Invoice Summary fields, top of the grid downwards. */
export type SummaryField =
  | 'company'
  | 'vendor'
  | 'internalId'
  | 'externalId'
  | 'invoiceDate'
  | 'uploadedDate'
  | 'tax'
  | 'shipping'
  | 'subtotal'
  | 'total'

export const SUMMARY_FIELDS: readonly SummaryField[] = [
  'company',
  'vendor',
  'internalId',
  'externalId',
  'invoiceDate',
  'uploadedDate',
  'tax',
  'shipping',
  'subtotal',
  'total',
]

/** The four amounts, which the reconcile flag ties together (D-148). */
export const AMOUNT_FIELDS: readonly SummaryField[] = ['tax', 'shipping', 'subtotal', 'total']

/** The editable fields on a line item row. */
export type LineField = 'description' | 'account' | 'department' | 'price' | 'quantity'

export const LINE_FIELDS: readonly LineField[] = ['description', 'account', 'department', 'price', 'quantity']

/**
 * Low Invoice Confidence: every Invoice Summary field the AI reads (owner,
 * 2026-09-30) — so not Internal ID, which the system generates, nor Uploaded
 * Date, which is the day the document arrived. Owner, 2026-10-04 (D-292):
 * "Internal ID and Uploaded Date could probably do without the yellow."
 */
const INVOICE_LOW_CONFIDENCE = 'InvoiceExtractionLowConfidence'
/** Low Line Item Confidence: every field on the line it names. */
const LINE_LOW_CONFIDENCE = 'LineItemExtractionLowConfidence'
/** Amounts Don't Reconcile: the four amounts together. */
const AMOUNTS_MISMATCH = 'LineItemsToTotalMismatch'

/**
 * WHICH FLAGS TURN EACH INVOICE SUMMARY FIELD YELLOW. Each one asks the
 * reviewer to check one value that is already there.
 */
export const SUMMARY_FIELD_FLAGS: Readonly<Record<SummaryField, readonly string[]>> = {
  company: [INVOICE_LOW_CONFIDENCE, 'CompanyMatchLowConfidence', 'CompanyInferredFromLetterhead'],
  vendor: [INVOICE_LOW_CONFIDENCE, 'VendorMatchLowConfidence', 'DuplicateVendor'],
  internalId: [],
  externalId: [INVOICE_LOW_CONFIDENCE],
  invoiceDate: [INVOICE_LOW_CONFIDENCE],
  uploadedDate: [],
  tax: [INVOICE_LOW_CONFIDENCE, AMOUNTS_MISMATCH],
  shipping: [INVOICE_LOW_CONFIDENCE, AMOUNTS_MISMATCH],
  subtotal: [INVOICE_LOW_CONFIDENCE, AMOUNTS_MISMATCH],
  total: [INVOICE_LOW_CONFIDENCE, AMOUNTS_MISMATCH],
}

/** Which per-line flags turn each field on THEIR line yellow. */
export const LINE_FIELD_FLAGS: Readonly<Record<LineField, readonly string[]>> = {
  description: [LINE_LOW_CONFIDENCE],
  account: [LINE_LOW_CONFIDENCE, 'AccountMatchLowConfidence'],
  department: [LINE_LOW_CONFIDENCE],
  price: [LINE_LOW_CONFIDENCE],
  quantity: [LINE_LOW_CONFIDENCE],
}

/**
 * THE FLAGS THAT TINT NO FIELD, deliberately. They stay in the Flags section
 * only. Every stored flag value is either in one of the two maps above or
 * here, which `app/mock/tests/red-yellow-borders.test.ts` checks.
 *
 *  - The Missing flags: the field is empty, so it is red by the required rule
 *    and never needs a yellow of its own. `MissingMarketingCategory`'s control
 *    is the line's Additional Detail icon, which takes the red border while the
 *    category is empty.
 *  - Whole-invoice flags: a duplicate, a credit, a non-invoice, a currency, a
 *    prior balance, too many lines, a failed match. No one field holds the
 *    answer.
 *  - Flags about two or more fields at once, or about a code rather than a
 *    field: Inter-Company Billing (Vendor and Company), Unrecognized Code and
 *    Stamped Code May Not Fit Every Line.
 *  - `UnmatchedTaggedEntity`: its controls are the line icons, which take the
 *    red border when a tag is unmatched.
 */
export const FLAGS_WITHOUT_A_FIELD: readonly string[] = [
  'MissingTotal',
  'MissingExternalInvoiceId',
  'MissingInvoiceDate',
  'MissingVendor',
  'MissingCompany',
  'MissingAccount',
  'MissingMarketingCategory',
  'UnmatchedTaggedEntity',
  'NonUSDCurrency',
  'DuplicateDeterministic',
  'DuplicateFuzzy',
  'CreditMemo',
  'UnrecognizedGLCode',
  'GLCodeLineMismatch',
  'NonInvoice',
  'InternalDocument',
  'MaximumLineItemsExtracted',
  'MatchingFailed',
  'PriorBalanceIncluded',
]

/** The part of a flag record this file reads. */
export interface FlagRecord {
  flag_type?: string | null
  detail?: string | null
  resolved_at?: string | null
}

function unresolved(flags: readonly FlagRecord[]): FlagRecord[] {
  return flags.filter((f) => !f.resolved_at)
}

/** Each flag type once, in the order the field's list names them. */
function inOrder(wanted: readonly string[], present: ReadonlySet<string>): string[] {
  return wanted.filter((t) => present.has(t))
}

/**
 * The unresolved flags that make an Invoice Summary field yellow.
 *
 * `amountsDoNotReconcile`: the live arithmetic says the four amounts do not
 * reconcile (D-148, D-151). It ties the amounts to the reconcile flag even
 * before one is raised — a reviewer's own edit that breaks the sum shows at
 * once — and an unresolved flag keeps them yellow after the sum is fixed,
 * until someone marks it resolved.
 */
export function summaryFieldFlags(
  flags: readonly FlagRecord[],
  field: SummaryField,
  options: { amountsDoNotReconcile?: boolean } = {},
): string[] {
  const present = new Set(unresolved(flags).map((f) => f.flag_type ?? ''))
  if (options.amountsDoNotReconcile) present.add(AMOUNTS_MISMATCH)
  return inOrder(SUMMARY_FIELD_FLAGS[field], present)
}

/**
 * The unresolved flags that make a field on one line yellow. A per-line flag
 * names its line by sequence in `detail` (`flagDetailSequence`). One whose
 * detail names no line covers every line, the same fallback the editor's
 * Missing Account check uses.
 */
export function lineFieldFlags(
  flags: readonly FlagRecord[],
  lineNumber: number | null | undefined,
  field: LineField,
): string[] {
  const present = new Set(
    unresolved(flags)
      .filter((f) => {
        const line = flagDetailSequence(f.detail)
        return line === null || line === lineNumber
      })
      .map((f) => f.flag_type ?? ''),
  )
  return inOrder(LINE_FIELD_FLAGS[field], present)
}

type FieldBorder = 'required' | 'flagged' | 'none'

/** THE ONE ORDER: red (required and empty) beats yellow (flagged). */
export function fieldBorder(requiredEmpty: boolean, flagTypes: readonly string[]): FieldBorder {
  if (requiredEmpty) return 'required'
  if (flagTypes.length > 0) return 'flagged'
  return 'none'
}

/** The hover text on a yellow field, naming its flags. Undefined when none. */
export function flaggedTooltip(flagTypes: readonly string[]): string | undefined {
  if (flagTypes.length === 0) return undefined
  const labels = flagTypes.map((t) => flagLabel(t))
  if (labels.length === 1) return `Unresolved flag: ${labels[0]}.`
  return `Unresolved flags: ${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}.`
}

/**
 * The Release gate's sentence for amounts that do not reconcile (D-151). The
 * editor lists it among the reasons Release is disabled, and a yellow amount
 * box shows it when only the live arithmetic made the box yellow.
 */
export const AMOUNTS_DO_NOT_RECONCILE =
  'The amounts don’t reconcile — the line items plus Tax plus Shipping must be within $0.03 of the Total.'

/**
 * THE HOVER TEXT ON AN INVOICE SUMMARY FIELD (QA 2026-10-01, D1-01). It names
 * only flags the invoice has. The live arithmetic turns the four amounts
 * yellow before any Amounts Don’t Reconcile flag exists (`summaryFieldFlags`),
 * and the tooltip used to name that flag anyway, so a reviewer looked in the
 * Flags section for a flag that was not there. When the yellow comes from the
 * arithmetic alone, the box says what the Release gate says. When the flag is
 * open, the box names it, as before. Undefined when the field is not yellow.
 */
export function summaryFieldTooltip(
  flags: readonly FlagRecord[],
  field: SummaryField,
  options: { amountsDoNotReconcile?: boolean } = {},
): string | undefined {
  const open = summaryFieldFlags(flags, field)
  const liveOnly =
    options.amountsDoNotReconcile === true && AMOUNT_FIELDS.includes(field) && !open.includes(AMOUNTS_MISMATCH)
  const parts = [flaggedTooltip(open), liveOnly ? AMOUNTS_DO_NOT_RECONCILE : undefined].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : undefined
}
